import React, { useState, useEffect, useCallback } from 'react';
import {
  Search, UserX, UserCheck, KeyRound, RefreshCw,
  ChevronDown, Filter, AlertCircle, CheckCircle2,
  Mail, Calendar, Shield, MoreVertical
} from 'lucide-react';
import { supabase } from '../utils/supabaseClient';
import '../styles/admin-users.css';

const UserManagement = () => {
  const [users, setUsers] = useState([]);
  const [filtered, setFiltered] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [roleFilter, setRoleFilter] = useState('all');
  const [actionMenu, setActionMenu] = useState(null);
  const [toast, setToast] = useState(null);
  const [confirmModal, setConfirmModal] = useState(null);

  const showToast = (message, type = 'success') => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 4000);
  };

  const fetchUsers = useCallback(async () => {
    setLoading(true);
    const { data, error } = await supabase
      .rpc('get_all_profiles');

    if (!error && data) {
      setUsers(data);
      setFiltered(data);
    }
    setLoading(false);
  }, []);

  useEffect(() => { fetchUsers(); }, [fetchUsers]);

  // Filter users
  useEffect(() => {
    let result = users;
    if (search) {
      const q = search.toLowerCase();
      result = result.filter(u =>
        u.email?.toLowerCase().includes(q) ||
        u.full_name?.toLowerCase().includes(q)
      );
    }
    if (roleFilter !== 'all') {
      result = result.filter(u => (u.role || 'user') === roleFilter);
    }
    setFiltered(result);
  }, [search, roleFilter, users]);

  // Block user
  const blockUser = async (userId) => {
    const { error } = await supabase
      .rpc('admin_update_user_role', {
        target_user_id: userId,
        new_role: 'blocked'
      });

    if (!error) {
      showToast('User has been blocked successfully.');
      fetchUsers();
    } else {
      showToast('Failed to block user. Please try again.', 'error');
    }
    setConfirmModal(null);
  };

  // Unblock user
  const unblockUser = async (userId) => {
    const { error } = await supabase
      .rpc('admin_update_user_role', {
        target_user_id: userId,
        new_role: 'user'
      });

    if (!error) {
      showToast('User has been unblocked successfully.');
      fetchUsers();
    } else {
      showToast('Failed to unblock user. Please try again.', 'error');
    }
    setConfirmModal(null);
  };

  // Make admin
  const makeAdmin = async (userId) => {
    const { error } = await supabase
      .rpc('admin_update_user_role', {
        target_user_id: userId,
        new_role: 'admin'
      });

    if (!error) {
      showToast('User has been granted admin privileges.');
      fetchUsers();
    } else {
      showToast('Failed to update role. Please try again.', 'error');
    }
    setConfirmModal(null);
  };

  // Send password reset
  const sendPasswordReset = async (email) => {
    const { error } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: `${window.location.origin.replace('admin.', 'app.')}/reset-password`,
    });

    if (!error) {
      showToast(`Password reset email sent to ${email}`);
    } else {
      showToast('Failed to send reset email. Please try again.', 'error');
    }
    setActionMenu(null);
  };

  const getRoleBadgeClass = (role) => {
    switch (role) {
      case 'admin': return 'badge-admin';
      case 'blocked': return 'badge-blocked';
      default: return 'badge-user';
    }
  };

  return (
    <div className="user-mgmt-page">
      {/* Toast */}
      {toast && (
        <div className={`admin-toast admin-toast-${toast.type}`}>
          {toast.type === 'success' ? <CheckCircle2 size={16} /> : <AlertCircle size={16} />}
          {toast.message}
        </div>
      )}

      {/* Confirm Modal */}
      {confirmModal && (
        <div className="admin-modal-overlay">
          <div className="admin-modal">
            <h3 className="admin-modal-title">{confirmModal.title}</h3>
            <p className="admin-modal-body">{confirmModal.message}</p>
            <div className="admin-modal-actions">
              <button className="admin-modal-cancel"
                onClick={() => setConfirmModal(null)}>Cancel</button>
              <button
                className={`admin-modal-confirm ${confirmModal.danger ? 'admin-modal-danger' : ''}`}
                onClick={confirmModal.action}>
                {confirmModal.confirmText}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Header */}
      <div className="user-mgmt-header">
        <div>
          <h1 className="admin-dash-title">User Management</h1>
          <p className="admin-dash-subtitle">
            {filtered.length} of {users.length} users
          </p>
        </div>
        <button className="admin-refresh-btn" onClick={fetchUsers}>
          <RefreshCw size={15} className={loading ? 'spinning' : ''} />
          Refresh
        </button>
      </div>

      {/* Filters */}
      <div className="user-filters">
        <div className="user-search-wrapper">
          <Search size={16} className="user-search-icon" />
          <input
            type="text" className="user-search-input"
            placeholder="Search by name or email..."
            value={search} onChange={e => setSearch(e.target.value)}
          />
        </div>

        <div className="user-filter-group">
          <Filter size={14} />
          <select className="user-filter-select"
            value={roleFilter} onChange={e => setRoleFilter(e.target.value)}>
            <option value="all">All roles</option>
            <option value="user">Users</option>
            <option value="admin">Admins</option>
            <option value="blocked">Blocked</option>
          </select>
          <ChevronDown size={14} className="select-arrow" />
        </div>
      </div>

      {/* Table */}
      <div className="user-table-card">
        {loading ? (
          <div className="user-table-loading">
            <div className="admin-spinner-lg" />
            <p>Loading users...</p>
          </div>
        ) : filtered.length === 0 ? (
          <div className="user-table-empty">
            <Shield size={32} color="#333" />
            <p>No users found matching your search.</p>
          </div>
        ) : (
          <div className="user-table-wrapper">
            <table className="user-table">
              <thead>
                <tr>
                  <th>User</th>
                  <th>Email</th>
                  <th>Role</th>
                  <th>Joined</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map(user => (
                  <tr key={user.id} className="user-table-row">
                    <td>
                      <div className="user-cell">
                        <div className="user-cell-avatar">
                          {(user.full_name || user.email || 'U')[0].toUpperCase()}
                        </div>
                        <span className="user-cell-name">
                          {user.full_name || 'No name set'}
                        </span>
                      </div>
                    </td>
                    <td>
                      <div className="user-email-cell">
                        <Mail size={13} />{user.email}
                      </div>
                    </td>
                    <td>
                      <span className={`user-role-badge ${getRoleBadgeClass(user.role || 'user')}`}>
                        {user.role || 'user'}
                      </span>
                    </td>
                    <td>
                      <div className="user-date-cell">
                        <Calendar size={13} />
                        {new Date(user.created_at).toLocaleDateString('en-US', {
                          year: 'numeric', month: 'short', day: 'numeric'
                        })}
                      </div>
                    </td>
                    <td>
                      <div className="user-actions-cell">
                        {/* Quick action buttons */}
                        {(user.role !== 'blocked') ? (
                          <button className="user-action-btn user-action-danger"
                            title="Block user"
                            onClick={() => setConfirmModal({
                              title: 'Block User',
                              message: `Are you sure you want to block ${user.email}? They will not be able to log in.`,
                              confirmText: 'Block User',
                              danger: true,
                              action: () => blockUser(user.id)
                            })}>
                            <UserX size={14} />
                          </button>
                        ) : (
                          <button className="user-action-btn user-action-success"
                            title="Unblock user"
                            onClick={() => setConfirmModal({
                              title: 'Unblock User',
                              message: `Are you sure you want to unblock ${user.email}? They will regain access to their account.`,
                              confirmText: 'Unblock User',
                              danger: false,
                              action: () => unblockUser(user.id)
                            })}>
                            <UserCheck size={14} />
                          </button>
                        )}

                        <button className="user-action-btn"
                          title="Send password reset"
                          onClick={() => sendPasswordReset(user.email)}>
                          <KeyRound size={14} />
                        </button>

                        {/* More menu */}
                        <div style={{ position: 'relative' }}>
                          <button className="user-action-btn"
                            onClick={() => setActionMenu(actionMenu === user.id ? null : user.id)}>
                            <MoreVertical size={14} />
                          </button>

                          {actionMenu === user.id && (
                            <div className="user-action-dropdown">
                              {user.role !== 'admin' && (
                                <button className="user-dropdown-item"
                                  onClick={() => {
                                    setActionMenu(null);
                                    setConfirmModal({
                                      title: 'Grant Admin Access',
                                      message: `Are you sure you want to make ${user.email} an administrator? They will have full access to this admin portal.`,
                                      confirmText: 'Grant Admin',
                                      danger: false,
                                      action: () => makeAdmin(user.id)
                                    });
                                  }}>
                                  <Shield size={13} /> Make Admin
                                </button>
                              )}
                              <button className="user-dropdown-item"
                                onClick={() => sendPasswordReset(user.email)}>
                                <KeyRound size={13} /> Reset Password
                              </button>
                            </div>
                          )}
                        </div>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default UserManagement;
